import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  BENCH_ORDERS,
  BENCH_REFINEMENT,
  benchOrderLabels,
  benchSummary,
  benchTransition,
  FAMILIES_REFINEMENT,
  FAMILY_MODELS,
  FAMILY_QUESTIONS,
  familiesSummary,
  familiesTransition,
  familyAnswer,
  familyModelLabels,
  familyQuestionLabels,
  familyQuestionText,
  initialBench,
  initialFamilies,
  initialPreference,
  initialStages,
  latencyText,
  listPrices,
  NEW_QUESTION,
  NO_NEW_KNOWLEDGE,
  ordersMatch,
  orderedBench,
  PREFERENCE_QUESTION,
  PREFERENCE_REFINEMENT,
  preferenceSummary,
  preferenceTransition,
  priceText,
  RATERS,
  ratingDone,
  ratings,
  REPLY_A,
  REPLY_B,
  STAGE_PROMPTS,
  STAGES,
  STAGES_REFINEMENT,
  stageLabels,
  stagePromptLabels,
  stageReply,
  stagesSummary,
  stagesTransition,
  toBenchOrder,
  toFamilyModel,
  toFamilyQuestion,
  tokensText,
  topByScore,
  topBySize,
  toStage,
  toStagePrompt,
  trainedToward,
  tunedReply,
  WHY_ZERO,
  yesNo,
  type BenchAction,
  type FamiliesAction,
  type PreferenceAction,
  type StagesAction,
} from './made-models';

import './relationship-labs.css';
import './made-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const SIMULATED_STAGES = 'This graphic is a deterministic simulation of three training stages.';
const SIMULATED_RATERS = 'This graphic is a deterministic simulation of five raters.';
const REPLAY = 'This graphic replays real runs.';

export function MadeStagesLab() {
  const [state, setState] = useState(initialStages);
  const act = (action: StagesAction) => setState((current) => stagesTransition(current, action));
  const summary = stagesSummary(state);
  const entry = stageReply(state);
  return (
    <Lab
      title="What does each training stage change about the reply?"
      eyebrow="How a model is made · three stages"
      className="rel-lab made-lab"
    >
      <Predict>
        What does each training stage change about the reply? Choose a Stage and a Prompt, then
        press Run.
      </Predict>
      <div className="cb-controls">
        <label>
          Stage
          <ModelSelect
            aria-label="Stage"
            value={state.stage}
            onValueChange={(value) => act({ type: 'stage', value: toStage(value) })}
            options={STAGES.map((id) => ({ value: id, label: stageLabels[id] }))}
          />
        </label>
        <label>
          Prompt
          <ModelSelect
            aria-label="Prompt"
            value={state.prompt}
            onValueChange={(value) => act({ type: 'prompt', value: toStagePrompt(value) })}
            options={STAGE_PROMPTS.map((id) => ({ value: id, label: stagePromptLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The reply">
        <pre aria-label="The reply">{state.run ? entry.reply : '(not run yet)'}</pre>
      </Panel>
      <Panel label="What the stage changed">
        <pre aria-label="What the stage changed">{state.run ? entry.changed : '(not run yet)'}</pre>
      </Panel>
      {state.run ? (
        <Facts
          values={[
            ['Answered', yesNo(entry.answered)],
            ['Tone', entry.tone],
            ['New knowledge', NO_NEW_KNOWLEDGE],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? <p className="rel-caption">{STAGES_REFINEMENT}</p> : null}
      <p className="rel-caption">{SIMULATED_STAGES}</p>
    </Lab>
  );
}

export function MadePreferenceLab() {
  const [state, setState] = useState(initialPreference);
  const act = (action: PreferenceAction) =>
    setState((current) => preferenceTransition(current, action));
  const summary = preferenceSummary(state);
  const done = ratingDone(state);
  const facts: [string, string][] = [
    ['Prefer A', `${state.a} of ${RATERS}`],
    ['Prefer B', `${state.b} of ${RATERS}`],
  ];
  if (state.trained) facts.push(['Trained toward', trainedToward(state)]);
  return (
    <Lab
      title="What does a model learn from what people prefer?"
      eyebrow="How a model is made · what raters teach"
      className="rel-lab made-lab"
    >
      <Predict>
        What will the trained model say when it has no data? Press Rate: prefer A or Rate: prefer B
        five times, then Train.
      </Predict>
      <Panel label={`The question: ${PREFERENCE_QUESTION}`}>
        <div className="made-replies">
          <pre aria-label="Reply A">{`A: ${REPLY_A}`}</pre>
          <pre aria-label="Reply B">{`B: ${REPLY_B}`}</pre>
        </div>
      </Panel>
      <div className="cb-controls">
        <Button
          size="sm"
          variant="secondary"
          disabled={done || state.trained}
          onClick={() => act({ type: 'prefer', value: 'A' })}
        >
          Rate: prefer A
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={done || state.trained}
          onClick={() => act({ type: 'prefer', value: 'B' })}
        >
          Rate: prefer B
        </Button>
        <Button size="sm" disabled={!done || state.trained} onClick={() => act({ type: 'train' })}>
          Train
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The tuned model on a new question">
        <pre aria-label="The tuned model on a new question">
          {state.trained ? `Q: ${NEW_QUESTION}\nA: ${tunedReply(state)}` : '(not trained yet)'}
        </pre>
      </Panel>
      {ratings(state) > 0 ? <Facts values={facts} /> : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.trained ? <p className="rel-caption">{PREFERENCE_REFINEMENT}</p> : null}
      <p className="rel-caption">{SIMULATED_RATERS}</p>
    </Lab>
  );
}

export function MadeFamiliesLab() {
  const [state, setState] = useState(initialFamilies);
  const act = (action: FamiliesAction) =>
    setState((current) => familiesTransition(current, action));
  const summary = familiesSummary(state);
  const entry = familyAnswer(state);
  return (
    <Lab
      title="How do four models of different sizes answer the same question?"
      eyebrow="How a model is made · families and sizes"
      className="rel-lab made-lab"
    >
      <Predict>
        How do four models of different sizes answer the same question? Choose a Model and a
        Question, then press Ask.
      </Predict>
      <div className="cb-controls">
        <label>
          Model
          <ModelSelect
            aria-label="Model"
            value={state.model}
            onValueChange={(value) => act({ type: 'model', value: toFamilyModel(value) })}
            options={FAMILY_MODELS.map((id) => ({ value: id, label: familyModelLabels[id] }))}
          />
        </label>
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toFamilyQuestion(value) })}
            options={FAMILY_QUESTIONS.map((id) => ({
              value: id,
              label: familyQuestionLabels[id],
            }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{familyQuestionText[state.question]}</pre>
      </Panel>
      <Panel label="The reply">
        <pre aria-label="The reply">{state.asked ? entry.reply : '(not asked yet)'}</pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Latency', latencyText(entry.ms)],
            ['List price per million', priceText(listPrices[state.model])],
            ['Tokens', tokensText(entry)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.asked ? <p className="rel-caption">{FAMILIES_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function MadeBenchmarkLab() {
  const [state, setState] = useState(initialBench);
  const act = (action: BenchAction) => setState((current) => benchTransition(current, action));
  const summary = benchSummary(state);
  return (
    <Lab
      title="Does the leaderboard order match Maya's notes?"
      eyebrow="How a model is made · the leaderboard and the notes"
      className="rel-lab made-lab"
    >
      <Predict>
        Does the leaderboard order match Maya's notes? Choose Order by, then press Show.
      </Predict>
      <div className="cb-controls">
        <label>
          Order by
          <ModelSelect
            aria-label="Order by"
            value={state.order}
            onValueChange={(value) => act({ type: 'order', value: toBenchOrder(value) })}
            options={BENCH_ORDERS.map((id) => ({ value: id, label: benchOrderLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.why}
          onClick={() => act({ type: 'toggle-why' })}
        >
          Show why gpt-oss-120b scored 0
        </Button>
        <Button size="sm" disabled={state.shown} onClick={() => act({ type: 'show' })}>
          Show
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Four models"
        columns={['Model', 'Size', 'Passed of 30']}
        template="minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 0.8fr)"
        rows={
          state.shown
            ? orderedBench(state.order).map((row) => [
                familyModelLabels[row.model],
                row.size,
                row.passed,
              ])
            : []
        }
      />
      {state.why ? (
        <Panel label="Why gpt-oss-120b scored 0">
          <pre aria-label="Why gpt-oss-120b scored 0">{WHY_ZERO}</pre>
        </Panel>
      ) : null}
      {state.shown ? (
        <Facts
          values={[
            ['Top by leaderboard', topBySize()],
            ['Top by the notes', topByScore()],
            ['Orders match', yesNo(ordersMatch())],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.shown ? <p className="rel-caption">{BENCH_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
